<template>
  <div>
    <b-card>
      <b-card-text> 
        Use fun names for your discount code and give it a brief but meaningful description.<br />
        Note: Although the code is uppercase, it will be case insensitive when used.
      </b-card-text>
    </b-card>
    
    <b-card>
      <b-card-text>
        <b-form-checkbox
          id="useSelectedEvents"
          v-model="useSelectedEvents"
          name="useSelectedEvents"
          :value="true"
          :unchecked-value="false"
        >
          Valid for Selected Events Only <small>(Defaults to All Events until at least one event is chosen)</small>
        </b-form-checkbox>
        <keep-alive><FormSelectEvent
          ref="formSelectEvent"
          class="mt-2"
          v-if="useSelectedEvents"
          @input="addToSelectedEvents"
        ></FormSelectEvent></keep-alive>

        <b-list-group v-if="useSelectedEvents">
          <b-list-group-item v-for="event in selectedEvents"><b-button variant="danger" size="sm" @click="removeFromSelectedEvents">X</b-button> {{ event.title }}</b-list-group-item>
        </b-list-group>
      </b-card-text>
    </b-card>
    
    <b-card>
      <b-card-text>
        <b-form-checkbox
          id="singleUse"
          v-model="discount.singleUse"
          name="singleUse"
          :value="true"
          :unchecked-value="false"
        >
          Single-use code <small class="text-danger font-weight-bold"><i class="fa fa-exclamation-triangle"></i> Once used, the code will no longer be valid!</small>
        </b-form-checkbox>
      </b-card-text>
    </b-card>
    

    <b-form @submit="saveDiscountCode" class="mt-2" inline>
      <label class="sr-only" for="dc-form-input-code">Code</label>
      <b-input
        id="dc-form-input-code"
        v-model="discount.code"
        class="mb-2 mr-sm-2 mb-sm-0"
        placeholder="e.g. FIRSTTIME"
        maxlength="14"
        v-uppercasenospace
        required
      ></b-input>
      <label class="sr-only" for="dc-form-input-amount">Amount</label>
      <b-input
        id="dc-form-input-amount"
        v-model="discount.amount"
        class="mb-2 mr-sm-2 mb-sm-0"
        placeholder="e.g. 15"
        type="number"
        min="1"
        max="999"
        number
        required
      ></b-input>
      <label class="sr-only" for="dc-form-input-type">Type</label>
      <b-form-select class="mb-2 mr-sm-2 mb-sm-0" v-model="discount.type" :options="typeOptions" required></b-form-select>
      <label class="sr-only" for="dc-form-input-code">Description</label>
      <b-input
        id="dc-form-input-description"
        v-model="discount.description"
        class="mb-2 mr-sm-2 mb-sm-0"
        placeholder="e.g. first-timers discount"
        maxlength="30"
        required
      ></b-input>
      <label for="dc-form-input-code">Expires:&nbsp;</label>
      <b-input
        id="dc-form-input-dateExpires"
        type="date"
        v-model="discount.dateExpires"
        class="mb-2 mr-sm-2 mb-sm-0"
      ></b-input>
      <b-btn
        id="dc-form-input-submit"
        class="mb-2 mr-2 mb-sm-0"
        variant="success"
        type="submit"
      >Save</b-btn>
      <b-btn
        v-if="discount.id"
        id="dc-form-input-archive"
        class="mb-2 mr-sm-2 mb-sm-0"
        variant="warning"
        type="button"
        @click="archiveDiscountCode"
      >Archive</b-btn>
    </b-form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import FormSelectEvent from '@/components/FormSelectEvent.vue'
export default {
  name: 'DiscountCodeForm',
  components: {
    FormSelectEvent
  },
  data () {
    return {
      typeOptions: [
        { value: 'percent', text: 'Percent' },
        { value: 'dollar', text: 'Dollars' }
      ],
      useSelectedEvents: false,
      selectedEvents: [],
      discount: {
        id: null,
        code: null,
        amount: null,
        type: 'percent',
        description: '',
        events: [],
        dateExpires: null,
        singleUse: false
      }
    }
  },
  computed: {
    ...mapGetters(['events'])
  },
  methods: {
    ...mapActions({
      getDiscountCode: 'getDiscountCode',
      postDiscountCode: 'postDiscountCode',
      putDiscountCode: 'putDiscountCode',
      getEvents: 'getEvents'
    }),
    async loadEvents() {
      return await this.getEvents({
        business: this.currentBusiness.id,
        status: [1, 2]
      })
    },
    addToSelectedEvents (event) {
      if (event && this.discount.events.findIndex(e => e === event) === -1) {
        this.discount.events.push(event)
        this.$refs.formSelectEvent.value = null
      }
    },
    removeFromSelectedEvents (event) {
      this.discount.events.splice(this.discount.events.indexOf(event['@id']), 1)
    },
    saveDiscountCode (e) {
      e.preventDefault()
      if (this.discount.id) {
        this.putDiscountCode(this.discount).then(() => {
          this.showSuccess()
        })
      } else {
        this.discount.business = this.currentBusiness['@id']
        this.postDiscountCode(this.discount).then(resp => {
          this.showSuccess()
          this.$router.push({ name: 'discountCodeForm', params: { did: resp.id } })
        })
      }
    },
    archiveDiscountCode () {
      if (confirm('Archive this discount code?')) {
        this.putDiscountCode({
          id: this.discount.id,
          active: false
        })
      }
    },
    async doGetDiscountCode () {
      const discountCode = await this.getDiscountCode({
        id: this.$route.params.did
      })

      discountCode.dateExpires = discountCode.dateExpires ? this.$moment(discountCode.dateExpires).format('YYYY-MM-DD') : null
      discountCode.events = discountCode.events.map(e => e['@id'])
      this.discount = discountCode
      return discountCode
    }
  },
  directives: {
    uppercasenospace: {
      bind(el, _, vnode) {
        el.addEventListener('keyup', e => {
          e.target.value = e.target.value.toUpperCase().replace(/\s/, '')
          vnode.componentInstance.$emit('input', e.target.value.toUpperCase().replace(/\s/, ''))
        })
      }
    }
  },
  watch: {
    '$route' (to) {
      this.doGetDiscountCode()
    },
    useSelectedEvents (val) {
      if (!val) {
        this.selectedEvents = []
        this.discount.events = []
      }
    },
    discount: {
      handler (val) {
        if (val.events && val.events.length) {
          this.useSelectedEvents = true
          this.selectedEvents = this.events.filter(e => val.events.includes(e['@id']))
        } else {
          this.selectedEvents = []
        }
      },
      deep: true
    }
  },
  async mounted () {
    await this.loadEvents()
    if (this.$route.params.did) {
      this.doGetDiscountCode()
    }
  }
}
</script>